import {
  ChatClientTagType,
  DateRangeFilter,
} from "graphql/apollo-graphql-generated/globalTypes";
import { ChatSelectButtonsOrderBy } from "pages/Chats/ChatOnlinePage/components/ChatSelectButtons";
import { reportToBugsnag } from 'utils/reportToBugsnag';

/** current version for the user settings, used to check for compatibility of saved settings */
export const USER_SETTINGS_CURRENT_VERSION = 1;

export interface UserSettingsInterface {
  /** current version for the user settings, used to check for compatibility of saved settings */
  version: number;
  chatOnlineSettings: ChatOnlineSettings;
  chatHistoryCardSettings: ChatHistoryCardSettings;
}

export interface ChatOnlineSettings {
  chatFilters: ChatOnlineSettingsFilters;
  enableNotifications: boolean;
  filterNotifications: boolean;
  chatsToDisableUserName: string[];
  messagesLimitToRender: number;
}

export interface ChatOnlineSettingsFilters {
  externalChatChannelIds?: string[];
  chatsOrderBy: ChatSelectButtonsOrderBy;
  /**
   * Filter scheduled messages: true (only scheduled), false (exclude scheduled), null/undefined (no filter)
   */
  scheduleMessagesFilter?: boolean | null;
  /**
   * @deprecated use scheduleMessagesFilter instead
   */
  hideScheduleMessages?: boolean;
  showBotMessages: boolean;
  showNotInteractedClients: boolean;
  /** show users shared with tags in chat select buttons */
  showUsersSharedWithTags: boolean;
  /** show time without contact in chat select buttons */
  showTimeWithoutContact: boolean;
  /** only show clients from the users with specified ids */
  usersSharedWithIdFilter?: string[];
  /** only show clients that are not shared with users */
  showWithoutSharedWith?: boolean;
  tagsFilter?: ChatClientTagType[];
  funnelStepIdFilter?: string[];
  showFunnelStepColor?: boolean;
}

export const chatOnlineSettingsFiltersDefault: ChatOnlineSettingsFilters = {
  chatsOrderBy: "DATE_DESC",
  scheduleMessagesFilter: !!process.env.REACT_APP_WHITELABEL_PARTNER ? null : false,
  showBotMessages: true,
  showNotInteractedClients: false,
  showUsersSharedWithTags: true,
  showTimeWithoutContact: true,
  usersSharedWithIdFilter: [],
  showWithoutSharedWith: false,
  tagsFilter: [],
  funnelStepIdFilter: null,
  showFunnelStepColor: true,
};

export const selectedSpreadSheetColumnsDefault: ClientsSpreadSheetColumns = {
  id: false,
  createdAtDate: true,
  createdAtHour: true,
  updatedAtDate: true,
  updatedAtHour: true,
  agency: true,
  agents: true,
  internalId: false,
  name: true,
  phone: true,
  email: true,
  document: false,
  idContrato: false,
  codigoRecibo: false,
  tags: false,
  origin: true,
  registrationChannel: true,
  dealType: false,
  funnelStep: true,
  preferredRealEstate: true,
  url: true,
  civilState: false,
  birthDate: false,
  occupation: false,
  cep: false,
  condominium: false,
  unit: false,
}

export type ClientsSpreadSheetColumns = {
  id: boolean
  createdAtDate: boolean
  createdAtHour: boolean
  updatedAtDate: boolean
  updatedAtHour: boolean
  agency: boolean
  agents: boolean
  internalId: boolean
  name: boolean
  phone: boolean
  email: boolean
  document: boolean
  idContrato: boolean
  codigoRecibo: boolean
  tags: boolean
  origin: boolean
  registrationChannel: boolean
  dealType: boolean
  funnelStep: boolean
  preferredRealEstate: boolean
  url: boolean
  civilState: boolean
  birthDate: boolean
  occupation: boolean
  cep: boolean
  condominium: boolean
  unit: boolean
}

export interface ChatHistoryCardSettings {
  selectedDateRangeDays?: number;
  showNoLeadClients?: boolean;
  dateRangeFilter?: DateRangeFilter;
  selectedSpreadSheetColumns?: ClientsSpreadSheetColumns;
}

export const defaultValues: Readonly<UserSettingsInterface> = {
  version: USER_SETTINGS_CURRENT_VERSION,
  chatOnlineSettings: {
    chatFilters: chatOnlineSettingsFiltersDefault,
    enableNotifications: false,
    filterNotifications: false,
    chatsToDisableUserName: [],
    messagesLimitToRender: 100,
  },
  chatHistoryCardSettings: {
    selectedSpreadSheetColumns: selectedSpreadSheetColumnsDefault,
  },
};

export class UserSettings implements UserSettingsInterface {
  static readonly LOCAL_STORAGE_KEY = "firefly-dashboard-user-settings";

  public version: number;
  public chatOnlineSettings: ChatOnlineSettings;
  public chatHistoryCardSettings: ChatHistoryCardSettings;
  public selectedSpreadSheetColumns: ClientsSpreadSheetColumns;

  constructor(userSettingsInterface: UserSettingsInterface) {
    Object.assign(this, userSettingsInterface);
  }

  public static fromLocalStorage = (): UserSettings => {
    const res = window.localStorage.getItem(UserSettings.LOCAL_STORAGE_KEY);
    if (!res) return null;

    try {
      const userSettingsInterface: UserSettingsInterface = JSON.parse(res);

      if (typeof userSettingsInterface !== "object") return null;

      return new UserSettings(userSettingsInterface);
    } catch (e: any) {
      reportToBugsnag({ error: e, caller: 'fromLocalStorage' });
      console.log(
        "[UserSettings] Error parsing UserSettings local storage object"
      );
      return null;
    }
  };

  public static fromDefaultValues = (): UserSettings => {
    return new UserSettings(defaultValues);
  };

  protected saveToLocalStorage = () => {
    window.localStorage.setItem(
      UserSettings.LOCAL_STORAGE_KEY,
      JSON.stringify(this)
    );
  };

  /** update chat online settings, changing only the existing values in the 'values' param */
  public updateChatOnlineSettings = (values: Partial<ChatOnlineSettings>) => {
    // console.log('updateChatOnlineSettings', { values })
    this.chatOnlineSettings = { ...this.chatOnlineSettings, ...values };
    this.saveToLocalStorage();
  };

  /** update chat history card settings, changing only the existing values in the 'values' param */
  public updateChatHistoryCardSettings = (
    values: Partial<ChatHistoryCardSettings>
  ) => {
    this.chatHistoryCardSettings = {
      ...this.chatHistoryCardSettings,
      ...values,
    };
    this.saveToLocalStorage();
  };
}
